import { CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { FileText, Image as ImageIcon } from "lucide-react";
import mime from "mime";
import type { FC } from "react";
import { useHandleChatAttachmentMutation } from "web_myplann/Api/Chat/useHandleChatAttachmentMutation";
import { ChatAttachmentFileButton } from "web_myplann/Chat/Components/Attachments/ChatAttachmentFileButton";
import type {
	ChatAttachmentFileStyle,
	ChatAttachmentView,
} from "web_myplann/Chat/Types/chatMessage";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { HandleDocumentTypes } from "web_myplann/Utils/WebViewModel";

type Props = {
	attachment: ChatAttachmentView;
	fileStyle: ChatAttachmentFileStyle;
	isUploading?: boolean;
};

export const ChatAttachmentFile: FC<Props> = ({
	attachment,
	fileStyle,
	isUploading = false,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const { mutate: openAttachment, isPending } =
		useHandleChatAttachmentMutation();

	const { id, fileName, mimeType, isImage } = attachment;
	const Icon = isImage ? ImageIcon : FileText;
	const canOpen = id !== undefined && mime.getExtension(mimeType) !== null;

	return (
		<ChatAttachmentFileButton
			disabled={isPending || !canOpen}
			onClick={() =>
				id !== undefined &&
				openAttachment({
					attachmentId: id,
					fileName,
					mimeType,
					actionType: HandleDocumentTypes.OPEN,
				})
			}
			backgroundColor={fileStyle.backgroundColor}
			boxShadow={fileStyle.boxShadow}
		>
			<IconBox
				bg={theme.palette.red[50]}
				size={32}
				borderRadius={theme.radii.md}
			>
				{isPending || isUploading ? (
					<CircularProgress
						size={theme.iconSize.sm}
						sx={{ color: theme.palette.red[500] }}
					/>
				) : (
					<Icon size={theme.iconSize.sm} color={theme.palette.red[500]} />
				)}
			</IconBox>
			<Stack minWidth={0}>
				<Typography variant="button" noWrap color={fileStyle.color}>
					{fileName}
				</Typography>
				<Typography variant="hint" color={fileStyle.secondaryColor}>
					{t(
						isImage
							? Resources.Chat.attachmentImage
							: Resources.Chat.attachmentFile,
					)}
				</Typography>
			</Stack>
		</ChatAttachmentFileButton>
	);
};
